import { useEffect, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  LinearProgress,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Email,
  CheckCircle,
  TrendingUp,
  Support,
  Send
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { supabase } from '../../services/supabase';
import './QuickOnboarding.scss';

interface ProfileSetupFormData {
  email?: string;
  showcases?: Array<{ id?: string | number; _id?: string; [key: string]: unknown }>;
  phoneNumber?: string;
  [key: string]: unknown;
}

interface ProfileSetupCompleteProps {
  formData?: ProfileSetupFormData;
  disableAutoRedirect?: boolean;
}

interface CompletedStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  completed: boolean;
}

interface NextStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: string;
  priority: 'high' | 'low';
}

interface Tip {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const QuickOnboardingSuccess: React.FC<ProfileSetupCompleteProps> = ({ formData: propFormData, disableAutoRedirect = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState<number>(10);

  // Email resend functionality
  const [isResending, setIsResending] = useState<boolean>(false);
  const [resendCountdown, setResendCountdown] = useState<number>(0);

  // Get form data from navigation state or props
  const formData: ProfileSetupFormData = (location.state as { formData?: ProfileSetupFormData } | null)?.formData || propFormData || {};

  // Resend email verification function
  const handleResendEmailVerification = async (): Promise<void> => {
    if (resendCountdown > 0 || isResending) return;

    setIsResending(true);
    try {
      const email = formData.email;
      if (!email) {
        toast.error(t('quickOnboarding.success.noEmail', 'Email address not found. Please contact support.'));
        return;
      }

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/quick-email-verification-success`
        }
      });

      if (error) {
        console.error('Supabase resend error:', error);
        toast.error(t('quickOnboarding.success.resendError', 'Failed to resend verification email: {{error}}', { error: error.message }));
      } else {
        toast.success(t('quickOnboarding.success.emailResent', 'Verification email sent successfully! Please check your inbox.'));

        // Start 60-second countdown before allowing another resend
        setResendCountdown(60);
        const countdownTimer = setInterval(() => {
          setResendCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownTimer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error: unknown) {
      console.error('Error resending email verification:', error);
      toast.error(t('quickOnboarding.success.resendError', 'Failed to resend verification email. Please try again later.'));
    } finally {
      setIsResending(false);
    }
  };

  // Auto redirect countdown (only if not disabled)
  useEffect(() => {
    if (disableAutoRedirect) {
      return; // Don't start countdown if auto-redirect is disabled
    }

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, disableAutoRedirect]);

  const completedSteps: CompletedStep[] = [
    {
      icon: <CheckCircle color="success" />,
      title: t('quickOnboarding.success.step1', 'Business Account Created'),
      description: t('quickOnboarding.success.step1Desc', 'Business account and staff profile set up'),
      completed: true
    },
    {
      icon: <CheckCircle color="success" />,
      title: t('quickOnboarding.success.step2', 'Showcases Added'),
      description: t('quickOnboarding.success.step2Desc', '{{count}} showcase(s) uploaded', {
        count: formData?.showcases?.length || 0
      }),
      completed: true
    },
    {
      icon: <CheckCircle color="success" />,
      title: t('quickOnboarding.success.step3', 'Profile Completed'),
      description: formData?.phoneNumber
        ? t('quickOnboarding.success.step3PhoneDesc', 'Profile completed with phone number')
        : t('quickOnboarding.success.step3Desc', 'Profile completed successfully'),
      completed: true
    }
  ];

  const nextSteps: NextStep[] = [
    {
      icon: <Email color="primary" />,
      title: t('quickOnboarding.success.verify', 'Verify Your Email'),
      description: t('quickOnboarding.success.verifyDesc', 'Check your email and click the verification link to activate your account'),
      action: t('quickOnboarding.success.checkEmail', 'Check Email'),
      priority: 'high'
    },
    {
      icon: <TrendingUp color="success" />,
      title: t('quickOnboarding.success.unlockReservation', '解鎖預約模式'),
      description: t('quickOnboarding.success.unlockReservationDesc', '啟用直接預約以擴大客戶群及增加收入'),
      action: t('quickOnboarding.success.activateReservation', 'Activate Now'),
      priority: 'low'
    }
  ];

  const tips: Tip[] = [];

  return (
    <Box
      className="profile-complete"
      sx={{
        minHeight: '100vh',
        py: 6
      }}
    >
      <Container maxWidth="lg">
                {/* Email Verification Alert - Moved to top */}
        <Alert severity="warning" className="email-verification-alert" sx={{ mb: 4, borderRadius: 3, maxWidth: '600px', mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('quickOnboarding.success.emailVerification', 'Important: Email Verification Required')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('quickOnboarding.success.emailVerificationDesc', 'We\'ve sent a verification email to {{email}}. Please check your inbox and click the verification link to activate your account and start receiving bookings.', { email: formData.email || 'your email' })}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('quickOnboarding.success.emailVerificationNote', 'Don\'t see the email? Check your spam folder or contact support for assistance.')}
          </Typography>

          {/* Resend Email Button */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={isResending ? <CircularProgress size={16} /> : <Send />}
              onClick={handleResendEmailVerification}
              disabled={isResending || resendCountdown > 0}
              sx={{
                minWidth: '200px',
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {isResending
                ? t('quickOnboarding.success.resending', 'Sending...')
                : resendCountdown > 0
                  ? t('quickOnboarding.success.resendWait', 'Resend in {{seconds}}s', { seconds: resendCountdown })
                  : t('quickOnboarding.success.resendEmail', 'Still not receive email? Send again')
              }
            </Button>
            {resendCountdown > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('quickOnboarding.success.waitMessage', 'Please wait before requesting another email')}
              </Typography>
            )}
          </Box>
        </Alert>

        {/* Auto-redirect notice (only show if auto-redirect is enabled) */}
        {!disableAutoRedirect && (
          <Alert severity="info" className="redirect-notice" sx={{ maxWidth: '500px', mx: 'auto', mb: 4 }}>
            <Typography variant="body2">
              {t('quickOnboarding.success.autoRedirect', 'Redirecting to dashboard in {{seconds}} seconds...', { seconds: countdown })}
            </Typography>
            <LinearProgress variant="determinate" value={(10 - countdown) * 10} sx={{ mt: 1 }} />
          </Alert>
        )}

        {/* Progress Summary */}
        <Paper elevation={2} className="content-section" sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom color="primary">
            {t('quickOnboarding.success.completed', 'What You\'ve Completed')}
          </Typography>
          <List>
            {completedSteps.map((step, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon>
                  {step.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {step.title}
                      </Typography>
                    </Box>
                  }
                  secondary={step.description}
                />
              </ListItem>
            ))}
          </List>
        </Paper>



        <Box sx={{ display: 'grid', grid: 'auto / 1fr', gap: 4, mb: 4 }}>
          {/* Next Steps */}
          <Paper elevation={2} className="content-section" sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              {t('quickOnboarding.success.nextSteps', 'Next Steps')}
            </Typography>
            <List>
              {nextSteps.map((step, index) => (
                <ListItem key={index} sx={{ py: 1, px: 0 }}>
                  <ListItemIcon>
                    {step.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {step.title}
                        </Typography>
                        {step.priority === 'high' && (
                          <Chip
                            label={t('quickOnboarding.success.urgent', '緊急')}
                            color="error"
                            size="small"
                          />
                        )}
                      </Box>
                    }
                    secondary={step.description}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Pro Tips */}
          {tips.length > 0 && (
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <Typography variant="h5" gutterBottom color="primary">
                {t('quickOnboarding.success.proTips', 'Pro Tips')}
              </Typography>
              <List>
                {tips.map((tip, index) => (
                  <ListItem key={index} sx={{ py: 1, px: 0 }}>
                    <ListItemIcon>
                      {tip.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {tip.title}
                        </Typography>
                      }
                      secondary={tip.description}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        {/* Contact Support */}
        <Box className="support-section" sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 4 }}>
          <a
            href="mailto:salon-enquiry@hkstylist.com?subject=Support%20Request&body=Hello%2C%20I%20need%20assistance%20with%20my%20HKStylist%20account."
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              textDecoration: 'none',
              color: '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
              const target = e.target as HTMLElement;
              target.style.backgroundColor = '#1976d2';
              target.style.color = 'white';
            }}
            onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
              const target = e.target as HTMLElement;
              target.style.backgroundColor = 'transparent';
              target.style.color = '#1976d2';
            }}
          >
            <Support style={{ fontSize: '20px' }} />
            {t('quickOnboarding.success.contactSupport', 'Contact Support')}: salon-enquiry@hkstylist.com
          </a>
        </Box>


      </Container>
    </Box>
  );
};

export default QuickOnboardingSuccess;
